/*
 * Theme controller — light / dark for the C+AI design language.
 *
 * Source of truth for the active theme:
 *   1. an explicit user choice persisted in localStorage (`clarity.theme`), else
 *   2. the device `prefers-color-scheme`.
 * The chosen theme is applied as `data-theme` on <html>. A no-flash inline
 * snippet in index.html sets the initial value before first paint; this module
 * keeps it in sync at runtime, persists explicit choices, and follows the device
 * preference while no explicit choice is stored.
 */

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'clarity.theme';

/** Ground colors per theme, kept in step with tokens.css for the theme-color meta. */
const THEME_COLOR: Record<Theme, string> = {
  light: '#fff6ef',
  dark: '#141221',
};

function media(): MediaQueryList | null {
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)')
    : null;
}

function storedChoice(): Theme | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'light' || v === 'dark' ? v : null;
  } catch {
    return null;
  }
}

/** The theme to show right now: explicit choice, else the device preference. */
export function resolveTheme(): Theme {
  return storedChoice() ?? (media()?.matches ? 'dark' : 'light');
}

function apply(theme: Theme): void {
  document.documentElement.dataset.theme = theme;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', THEME_COLOR[theme]);
}

const listeners = new Set<(t: Theme) => void>();

/** Subscribe to theme changes (toggle or device preference). Returns an unsubscribe. */
export function onThemeChange(fn: (t: Theme) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(theme: Theme): void {
  for (const fn of listeners) fn(theme);
}

/**
 * Install the controller: apply the resolved theme and, while NO explicit choice
 * is stored, follow the device preference live. Call once at boot.
 */
export function initTheme(): void {
  apply(resolveTheme());
  const mq = media();
  mq?.addEventListener('change', () => {
    if (storedChoice() === null) {
      const t = resolveTheme();
      apply(t);
      emit(t);
    }
  });
}

/** Persist an explicit choice (overrides the device preference) and apply it. */
export function setTheme(theme: Theme): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore quota/private-mode */
  }
  apply(theme);
  emit(theme);
}

/** Flip between light and dark, persisting the result. */
export function toggleTheme(): Theme {
  const next: Theme = resolveTheme() === 'dark' ? 'light' : 'dark';
  setTheme(next);
  return next;
}
